import { useMemo } from 'react';
import { isEmpty, keyBy, mapValues, identity, keys } from 'lodash';
import {
    COMMON_FLAGS,
    FEATURE_FLAGS,
    useCommonConfig,
    useConfigMatch,
    useAccountConfig,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { DEFAULT_TAGS, OTHER_TAGS_KEY } from './consts';

// order publishers for the tooltip
const SPECIAL_ORDER_PUBS = [OTHER_TAGS_KEY];
const SPECIAL_ORDER_PUBS_MAP = mapValues(keyBy(SPECIAL_ORDER_PUBS, identity), v => SPECIAL_ORDER_PUBS.indexOf(v) + 1);

const sort = (a, b) => {
    try {
        const aSpecialOrder = SPECIAL_ORDER_PUBS_MAP[a];
        const bSpecialOrder = SPECIAL_ORDER_PUBS_MAP[b];
        // if neither are special order, sort by locale
        if (!aSpecialOrder && !bSpecialOrder) {
            return (a || '').localeCompare(b || '');
        }
        // if a is not a special order, it should be first
        if (!aSpecialOrder) {
            return -1;
        }
        // if b is not a special order, it should be first
        if (!bSpecialOrder) {
            return 1;
        }
        // if both are special order, sort by index
        return aSpecialOrder - bSpecialOrder;
    } catch (error) {
        console.error('Error in publisher sorting:', error);
        return 0;
    }
};

export const useCRATags = ({ data }) => {
    const rawTags = data?.craTags || DEFAULT_TAGS;

    const isAppleSecondReviewAutoApproved = useConfigMatch({
        [FEATURE_FLAGS.APPLE_SECOND_REVIEW_AUTO_APPROVED]: 'true',
    });

    const isAdvertiserBypassEnabled = useConfigMatch({
        [FEATURE_FLAGS.ADVERTISER_BYPASS_ENABLED]: 'true',
    });

    const { [FEATURE_FLAGS.CREATIVE_REPORT_HIDDEN_CRA_TAG_COMMENTS_LIST]: rawHiddenCraTagsComments } = useAccountConfig(
        [FEATURE_FLAGS.CREATIVE_REPORT_HIDDEN_CRA_TAG_COMMENTS_LIST]
    );

    const { [COMMON_FLAGS.HIDDEN_CRA_TAGS_LIST]: rawHiddenCraTagsList } = useCommonConfig([
        COMMON_FLAGS.HIDDEN_CRA_TAGS_LIST,
    ]);
    const { [COMMON_FLAGS.CRA_TAG_COMMENT_CONTENT_BLACKLIST]: rawCraTagCommentContentBlacklist } = useCommonConfig([
        COMMON_FLAGS.CRA_TAG_COMMENT_CONTENT_BLACKLIST,
    ]);

    const hiddenCraTagsSet = useMemo(() => {
        try {
            return new Set(
                (rawHiddenCraTagsList || '')
                    .split(',')
                    .filter(Boolean)
                    .map(v => parseInt(v.trim()))
                    .filter(v => !isNaN(v))
            );
        } catch (e) {
            console.error('Error parsing hidden CRA tags:', e);
            return new Set();
        }
    }, [rawHiddenCraTagsList]);
    const craTagCommentContentBlacklist = useMemo(() => {
        try {
            return (rawCraTagCommentContentBlacklist || '')
                .split(',')
                .filter(Boolean)
                .map(v => v.toLowerCase());
        } catch (e) {
            console.error('Error parsing hidden CRA tag comments list:', e);
            return [];
        }
    }, [rawCraTagCommentContentBlacklist]);

    const hiddenCraTagsCommentsSet = useMemo(() => {
        try {
            return new Set(
                (rawHiddenCraTagsComments || '')
                    .split(',')
                    .filter(Boolean)
                    .map(v => parseInt(v.trim()))
                    .filter(v => !isNaN(v))
            );
        } catch (e) {
            console.error('Error parsing hidden CRA tag comments:', e);
            return new Set();
        }
    }, [rawHiddenCraTagsComments]);

    const isCRATagsPermitted = usePermissions('ADS_CONSOLE_CRA_TAG_TOOLTIP');

    // craTags is a map of publishers to tags
    // publishers is an array of publishers
    // initialSelected is the initial selected publisher
    const { craTags, publishers, initialSelected } = useMemo(() => {
        try {
            // transform the raw tags into a map of publishers to tags
            const craTags = rawTags.reduce((res, { groupName, tagData = [] }) => {
                if (isAppleSecondReviewAutoApproved && groupName === 'APPLE') {
                    return res;
                }

                const filteredTagData = tagData
                    .filter(({ tag }) => tag && !hiddenCraTagsSet.has(tag.id))
                    .map(({ tag, comment: rawComment }) => {
                        let comment = rawComment;
                        const lowerCaseComment = rawComment?.toLowerCase();
                        const isCommentBlacklisted =
                            lowerCaseComment &&
                            craTagCommentContentBlacklist.some(blacklistedComment =>
                                lowerCaseComment.includes(blacklistedComment)
                            );
                        const isTagCommentHidden = hiddenCraTagsCommentsSet.has(tag.id);
                        if (isTagCommentHidden || isCommentBlacklisted) {
                            comment = null;
                        }

                        return {
                            id: tag.id,
                            tag: tag.name,
                            comment,
                        };
                    });

                if (filteredTagData.length > 0) {
                    res[groupName] = new Set(filteredTagData);
                }
                return res;
            }, {});

            // make array of the publishers and sort
            const publishers = keys(craTags).sort(sort);
            const initialSelected = publishers[0] || null;

            return { craTags, publishers, initialSelected };
        } catch (error) {
            console.error('Error processing CRA tags:', error);
            return { craTags: {}, publishers: [], initialSelected: null };
        }
    }, [
        rawTags,
        isAppleSecondReviewAutoApproved,
        hiddenCraTagsSet,
        craTagCommentContentBlacklist,
        hiddenCraTagsCommentsSet,
    ]);

    const shouldRenderTooltip = useMemo(
        () => isCRATagsPermitted && !isEmpty(publishers) && !isAdvertiserBypassEnabled,
        [isCRATagsPermitted, publishers, isAdvertiserBypassEnabled]
    );

    return {
        shouldRenderTooltip,
        craTags,
        publishers,
        initialSelected,
    };
};
